import { createApi } from '@reduxjs/toolkit/query/react';
import type { UserSubscriptionType } from '../subscriptions.types';
import type {
  PaymentMethod,
  Purchase,
} from '../../../../../apps/frontend/services/users/users.types';
import { ogBaseQuery } from '../../ogBaseQuery';

export type LbSubscriptionType = {
  subscriptionType: UserSubscriptionType;
};

export type LbSubscriptionPlan = {
  id: string;
  interval: string;
  limit: number;
  name: string;
  pricePerSeat: number;
  setupFee: number;
  stripePlan: string;
  subtype: string;
  type: string;
  currency: string;
  product: string;
};

export const subscriptions = createApi({
  baseQuery: ogBaseQuery(),
  reducerPath: 'subscriptionsOG',
  endpoints: (builder) => ({
    getSubscriptionType: builder.query<
      LbSubscriptionType,
      { userId: string; skipInstitutionCheck?: boolean; cacheBustKey?: string }
    >({
      query: (data) => {
        return {
          url: `users/${data.userId}/getSubscriptionType`,
          method: 'GET',
          params: {
            skipInstitutionCheck: data.skipInstitutionCheck ?? true,
          },
        };
      },
    }),

    getSubscriptionPlans: builder.query<LbSubscriptionPlan[], void>({
      query: () => {
        return {
          url: 'purchases/plans',
          method: 'GET',
        };
      },
    }),
    validateCoupon: builder.mutation<
      {
        discountAmount?: string;
        amountOff?: boolean;
        valid: boolean;
      },
      {
        code: string;
        currency: string;
        product?: string;
        customerId?: string;
        amount?: number;
        priceId?: string;
      }
    >({
      query: (body) => {
        return {
          url: 'purchases/validateCoupon',
          method: 'POST',
          body,
        };
      },
      transformResponse: (data: { discount?: string; valid: boolean }) => {
        // data.discount could be 35USD or 35%
        if (data.discount !== undefined && !data.discount.includes('%')) {
          return {
            discountAmount: Number.parseFloat(data.discount).toString(),
            amountOff: true,
            valid: data.valid,
          };
        }

        return {
          ...data,
          discountAmount: data.discount,
        };
      },
    }),
    getTaxes: builder.query<
      { taxLocation: string; amount: number; currency: string },
      {
        email: string;
        planId: string;
        postalCode: string;
        countryCode: string;
        seats: number;
        coupon?: string;
      }
    >({
      query: (data) => {
        return {
          url: 'stripe/get-tax',
          method: 'POST',
          body: data,
        };
      },
    }),
    createPurchase: builder.mutation<
      Purchase,
      {
        captchaJWTToken: string;
        planId: string;
        seats: number;
        coupon?: string;
      }
    >({
      query: (data) => {
        return {
          url: 'purchases',
          method: 'POST',
          body: data,
        };
      },
    }),
    updatePurchase: builder.mutation<
      Purchase,
      {
        purchaseId: string | number;
        payload: {
          captchaJWTToken: string;
          planId: string;
          seats: number;
          coupon?: string;
          isCanceled: boolean;
        };
      }
    >({
      query: (data) => {
        return {
          url: `purchases/${data.purchaseId}`,
          method: 'PUT',
          body: data.payload,
        };
      },
    }),
    completePurchase: builder.mutation<
      Purchase,
      {
        paymentIntentId: string;
      }
    >({
      query: (data) => {
        return {
          url: 'purchases/completePurchase',
          method: 'POST',
          body: data,
        };
      },
    }),
    getCustomerPaymentMethod: builder.mutation<
      PaymentMethod,
      {
        customerId: string;
        paymentMethodId: string;
      }
    >({
      query: (data) => {
        return {
          url: 'stripe/get-customer-payment-method',
          method: 'POST',
          body: data,
        };
      },
    }),
  }),
});

export const {
  endpoints: { getSubscriptionType },
  useGetSubscriptionPlansQuery,
  useGetSubscriptionTypeQuery,
  useValidateCouponMutation,
  useGetTaxesQuery,
  useCreatePurchaseMutation,
  useUpdatePurchaseMutation,
  useGetCustomerPaymentMethodMutation,
  useCompletePurchaseMutation,
} = subscriptions;
